// src/firebase/firestore.js
import {
  collection, doc, getDoc, setDoc, addDoc, updateDoc,
  deleteDoc, query, where, orderBy, onSnapshot, serverTimestamp,
} from 'firebase/firestore';
import { db } from './config';

const USERS           = 'users';
const TASKS           = 'tasks';
const SHIPMENTS       = 'shipments';
const ACTIVITY        = 'activity_log';
const CONSIGNEES      = 'consignees';
const PENDING_INVITES = 'pending_invites';

// ─── USERS ────────────────────────────────────────────────────
export const createUser = (uid, data) =>
  setDoc(doc(db, USERS, uid), { ...data, updatedAt: serverTimestamp() }, { merge: true });

export const getUser = (uid) => getDoc(doc(db, USERS, uid));

export const updateUserRole = (uid, role) =>
  updateDoc(doc(db, USERS, uid), { role, updatedAt: serverTimestamp() });

export const deactivateUser = (uid) =>
  updateDoc(doc(db, USERS, uid), { status: 'inactive', deactivatedAt: serverTimestamp() });

// Returns all active users (excludes status === 'inactive')
export const subscribeUsers = (cb) =>
  onSnapshot(collection(db, USERS), snap =>
    cb(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(u => u.status !== 'inactive'))
  );

// ─── PENDING INVITES ──────────────────────────────────────────
// Stored by email (doc ID = lowercased email).
// On first Google sign-in, useAuth reads this and assigns the pre-set role.
export const createPendingInvite = (email, role, invitedBy, invitedByName) =>
  setDoc(doc(db, PENDING_INVITES, email.toLowerCase()), {
    email: email.toLowerCase(), role, invitedBy, invitedByName,
    createdAt: serverTimestamp(),
  });

export const getPendingInvite  = (email) =>
  getDoc(doc(db, PENDING_INVITES, email.toLowerCase()));

export const deletePendingInvite = (email) =>
  deleteDoc(doc(db, PENDING_INVITES, email.toLowerCase()));

export const subscribePendingInvites = (cb) =>
  onSnapshot(collection(db, PENDING_INVITES), snap =>
    cb(snap.docs.map(d => ({ id: d.id, ...d.data() })))
  );

// ─── TASKS ────────────────────────────────────────────────────
export const createTask = (data) =>
  addDoc(collection(db, TASKS), {
    ...data, createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
  });

export const updateTask = (id, data) => {
  const update = { ...data, updatedAt: serverTimestamp() };
  // Auto-stamp completedAt the first time a task is marked done
  if (data.status === 'done' && !data.completedAt) {
    update.completedAt = serverTimestamp();
  }
  return updateDoc(doc(db, TASKS, id), update);
};

export const deleteTask = (id) => deleteDoc(doc(db, TASKS, id));

export const reassignTask = (id, assigneeId, assigneeName, adminId, adminName) =>
  updateDoc(doc(db, TASKS, id), {
    assigneeId, assigneeName,
    reassignedBy: adminId, reassignedByName: adminName,
    updatedAt: serverTimestamp(),
  });

export const subscribeTasks = (cb) =>
  onSnapshot(
    query(collection(db, TASKS), orderBy('createdAt', 'desc')),
    snap => cb(snap.docs.map(d => ({ id: d.id, ...d.data() })))
  );

export const subscribeMyTasks = (uid, cb) =>
  onSnapshot(
    query(collection(db, TASKS), where('assigneeId', '==', uid), orderBy('createdAt', 'desc')),
    snap => cb(snap.docs.map(d => ({ id: d.id, ...d.data() })))
  );

// ─── SHIPMENTS ────────────────────────────────────────────────
export const createShipment = (data) =>
  addDoc(collection(db, SHIPMENTS), {
    ...data, createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
  });

export const updateShipment = (id, data) =>
  updateDoc(doc(db, SHIPMENTS, id), { ...data, updatedAt: serverTimestamp() });

export const deleteShipment = (id) => deleteDoc(doc(db, SHIPMENTS, id));

export const subscribeShipments = (cb) =>
  onSnapshot(
    query(collection(db, SHIPMENTS), orderBy('createdAt', 'desc')),
    snap => cb(snap.docs.map(d => ({ id: d.id, ...d.data() })))
  );

// ─── CONSIGNEES ───────────────────────────────────────────────
export const createConsignee = (data) =>
  addDoc(collection(db, CONSIGNEES), {
    ...data, createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
  });

export const updateConsignee = (id, data) =>
  updateDoc(doc(db, CONSIGNEES, id), { ...data, updatedAt: serverTimestamp() });

export const subscribeConsignees = (cb) =>
  onSnapshot(
    query(collection(db, CONSIGNEES), orderBy('createdAt', 'desc')),
    snap => cb(snap.docs.map(d => ({ id: d.id, ...d.data() })))
  );

// ─── ACTIVITY LOG ─────────────────────────────────────────────
export const logActivity = (data) =>
  addDoc(collection(db, ACTIVITY), { ...data, timestamp: serverTimestamp() });

export const subscribeActivity = (cb) =>
  onSnapshot(
    query(collection(db, ACTIVITY), orderBy('timestamp', 'desc')),
    snap => cb(snap.docs.map(d => ({ id: d.id, ...d.data() })))
  );

// ─── SETTINGS ─────────────────────────────────────────────────
export const subscribeSettingsDoc = (docId, cb) =>
  onSnapshot(
    doc(db, 'settings', docId),
    snap => cb(snap.exists() ? snap.data() : null),
    () => cb(null),
  );

export const saveSettingsDoc = (docId, data) =>
  setDoc(doc(db, 'settings', docId), data, { merge: true });
